export type WakeClipMeta = {
  phrase: string;
  score: number;
  label?: string;
};

/**
 * Encode mono Int16 PCM as a WAV Blob.
 */
export function encodeWav(pcm: Int16Array, sampleRate: number): Blob {
  const dataBytes = pcm.byteLength;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // PCM fmt chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeAscii(view, 36, "data");
  view.setUint32(40, dataBytes, true);

  new Int16Array(buffer, 44).set(pcm);
  return new Blob([buffer], { type: "audio/wav" });
}

export function downloadWakeClip(
  pcm: Int16Array,
  meta: WakeClipMeta,
  sampleRate = 16_000,
): string {
  const blob = encodeWav(pcm, sampleRate);
  const safePhrase = sanitizeFilename(meta.phrase || meta.label || "wake");
  const scorePct = Math.round(meta.score * 1000);
  const iso = new Date().toISOString().replace(/[:.]/g, "-");
  const filename = `wake-${safePhrase}-${scorePct}-${iso}.wav`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the browser has a chance to start the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return filename;
}

export function isWakeClipDebugEnabled(): boolean {
  try {
    return localStorage.getItem("wakeClipDebug") === "1";
  } catch {
    return false;
  }
}

function writeAscii(view: DataView, offset: number, text: string) {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i));
  }
}

function sanitizeFilename(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]+/g, "_").replace(/^_|_$/g, "") || "wake";
}
